import type {
  Insights,
  OutlierReason,
  OutlierTransaction,
  OutlierTransfersLeftOut,
  Transaction,
} from '@/modules/projects'
import { formatNumber, numberToCurrency } from '@/utils'

const OUTLIER_REASON_LABELS: Record<OutlierReason, string> = {
  largest_in: 'Largest in',
  largest_out: 'Largest out',
  one_off_large_payee: 'One-off large payee',
}

export const outlierReasonLabels = (reasons: OutlierReason[] = []): string =>
  reasons.map((reason) => OUTLIER_REASON_LABELS[reason]).join(', ')

// Insights from before the rerun carry rows without reasons and need the category table.
export const outliersHaveReasons = ({
  outliers,
  outlierTransfersLeftOut,
}: Pick<Insights, 'outliers' | 'outlierTransfersLeftOut'>): boolean =>
  outlierTransfersLeftOut?.count !== undefined ||
  outliers.some((outlier) => outlier.reasons)

export const filterOutlierTransactions =
  (outliers: OutlierTransaction[]) =>
  (transactions: Transaction[]): OutlierTransaction[] => {
    const reasonsById = new Map(
      outliers.map((outlier) => [outlier.id, outlier.reasons]),
    )
    return transactions
      .filter((transaction) => reasonsById.has(transaction.id))
      .map((transaction) => ({
        ...transaction,
        reasons: reasonsById.get(transaction.id),
      }))
  }

export const transfersLeftOutLine = ({
  count = 0,
  amount = 0,
}: OutlierTransfersLeftOut = {}): string | null =>
  count > 0
    ? `${formatNumber(count)} ${count === 1 ? 'move' : 'moves'} between project accounts left out, ${numberToCurrency(amount)}`
    : null

const ROUND_NUMBER_MULTIPLE = 100

export const isRoundNumberAmount = (amount: number): boolean => {
  if (amount === 0) return false
  return Math.abs(amount) % ROUND_NUMBER_MULTIPLE === 0
}

export const filterRoundNumberTransactions = (
  transactions: Transaction[],
): Transaction[] =>
  transactions.filter((transaction) => isRoundNumberAmount(transaction.amount))

const WEEKEND_DAYS = new Set([0, 6])

export const isWeekendDate = (date: string): boolean => {
  const datePart = date.slice(0, 10)
  const parsed = new Date(`${datePart}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return false
  return WEEKEND_DAYS.has(parsed.getUTCDay())
}

export const filterWeekendTransactions = (
  transactions: Transaction[],
): Transaction[] =>
  transactions.filter((transaction) => isWeekendDate(transaction.date))
